import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Button,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  InputAdornment,
  Box,
  Alert,
  CircularProgress,
  Avatar,
  Chip,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Description as DescriptionIcon,
} from "@mui/icons-material";
import { useAuth } from "../context/auth";
import PermissionGuard from "./PermissionGuard";
import ConfirmDialog from "./ConfirmDialog";

const EmployeeList = () => {
  const [employees, setEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteDialog, setDeleteDialog] = useState({ open: false, employeeId: null, employeeName: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const navigate = useNavigate();
  const { token, isAuthenticated } = useAuth();

  const baseUrl = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
    let isMounted = true;
    const source = axios.CancelToken.source();

    const fetchEmployees = async () => {
      try {
        setLoading(true);
        setError("");

        if (!isAuthenticated || !token) {
          setError("Please login to view employees");
          setLoading(false);
          return;
        }

        const response = await axios.get(`${baseUrl}/api/employees`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          cancelToken: source.token,
        });

        if (isMounted) {
          setEmployees(response.data.employees || []);
        }
      } catch (error) {
        if (isMounted && !axios.isCancel(error)) {
          console.error("Error fetching employees:", error);
          if (error.response?.status === 401) {
            setError("Authentication failed. Please login again.");
          } else if (error.response?.status === 403) {
            setError("You don't have permission to view employees.");
          } else {
            setError("Failed to load employees. Please try again.");
          }
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchEmployees();

    return () => {
      isMounted = false;
      source.cancel("Component unmounted. Request canceled.");
    };
  }, [baseUrl, token, isAuthenticated]);

  const handleAdd = () => navigate("/dashboard/employees/add");
  const handleEdit = (id) => navigate(`/dashboard/employees/edit/${id}`);
  const handleViewLetter = (id) => navigate(`/dashboard/employees/${id}/letter`);

  const handleOpenDialog = (id) => {
    const employee = employees.find(e => e.id === id);
    if (!employee) return;

    setDeleteDialog({
      open: true,
      employeeId: id,
      employeeName: `${employee.firstName} ${employee.lastName}`
    });
  };

  const handleCloseDialog = () => {
    setDeleteDialog({ open: false, employeeId: null, employeeName: '' });
    setDeleteLoading(false);
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await axios.delete(`${baseUrl}/api/employees/${deleteDialog.employeeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setEmployees(employees.filter((employee) => employee.id !== deleteDialog.employeeId));
      handleCloseDialog();
    } catch (error) {
      console.error("Error deleting employee:", error);
      setError(error.response?.data?.error || "Failed to delete employee. Please try again.");
      setDeleteLoading(false);
    }
  };

  const filteredEmployees = employees.filter((employee) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      employee.employeeId?.toLowerCase().includes(searchLower) ||
      employee.firstName?.toLowerCase().includes(searchLower) ||
      employee.lastName?.toLowerCase().includes(searchLower) ||
      employee.email?.toLowerCase().includes(searchLower) ||
      employee.phoneNumber?.toLowerCase().includes(searchLower) ||
      employee.jobTitle?.toLowerCase().includes(searchLower)
    );
  });

  if (!isAuthenticated) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
          Employee Management
        </Typography>
        <Alert severity="error" sx={{ mb: 2 }}>
          Please login to view employees.
        </Alert>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate("/login")}
        >
          Go to Login
        </Button>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
          Employee Management
        </Typography>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: { xs: 2, sm: 3 }, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
          Employee Management
        </Typography>
        <PermissionGuard resource="employee" action="create">
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAdd}
          >
            Add Employee
          </Button>
        </PermissionGuard>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <TextField
        fullWidth
        label="Search by ID, name, email, phone, or job title..."
        variant="outlined"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 3,
          '& .MuiInputBase-root': {
            fontSize: { xs: '0.875rem', sm: '1rem' }
          },
          '& .MuiInputLabel-root': {
            fontSize: { xs: '0.875rem', sm: '1rem' }
          }
        }}
      />

      {filteredEmployees.length === 0 && !loading ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          {employees.length === 0
            ? "No employees found. Click 'Add Employee' to create one."
            : "No employees match your search criteria."}
        </Alert>
      ) : (
        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: { xs: 650, md: 'auto' } }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#1976d2' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: { xs: '0.8rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>Photo</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: { xs: '0.8rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>Employee ID</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: { xs: '0.8rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>Name</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: { xs: '0.8rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>Email</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: { xs: '0.8rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>Phone</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: { xs: '0.8rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>Job Title</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: { xs: '0.8rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>Date of Hire</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: { xs: '0.8rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredEmployees.map((employee) => (
                <TableRow key={employee.id} hover>
                  <TableCell>
                    <Avatar
                      src={employee.profilePicture ? `${baseUrl}${employee.profilePicture}` : undefined}
                      alt={`${employee.firstName} ${employee.lastName}`}
                      sx={{ width: 40, height: 40 }}
                    >
                      {employee.firstName?.[0]}{employee.lastName?.[0]}
                    </Avatar>
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    <Chip label={employee.employeeId} size="small" color="primary" />
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    {employee.firstName} {employee.middleName ? `${employee.middleName} ` : ''}{employee.lastName}
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{employee.email}</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{employee.phoneNumber}</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{employee.jobTitle}</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    {new Date(employee.dateOfHire).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <PermissionGuard resource="employment_letter" action="create">
                        <IconButton
                          color="success"
                          onClick={() => handleViewLetter(employee.id)}
                          title="Generate Employment Letter"
                          size="small"
                        >
                          <DescriptionIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                        </IconButton>
                      </PermissionGuard>
                      <PermissionGuard resource="employee" action="edit">
                        <IconButton
                          color="primary"
                          onClick={() => handleEdit(employee.id)}
                          title="Edit Employee"
                          size="small"
                        >
                          <EditIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                        </IconButton>
                      </PermissionGuard>
                      <PermissionGuard resource="employee" action="delete">
                        <IconButton
                          color="error"
                          onClick={() => handleOpenDialog(employee.id)}
                          title="Delete Employee"
                          size="small"
                        >
                          <DeleteIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                        </IconButton>
                      </PermissionGuard>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialog.open}
        onClose={handleCloseDialog}
        onConfirm={handleDelete}
        title="Delete Employee"
        message={`Are you sure you want to delete employee "${deleteDialog.employeeName}"?`}
        warning="This action cannot be undone."
        severity="warning"
        loading={deleteLoading}
        confirmText="Delete Employee"
        confirmColor="error"
      />
    </Container>
  );
};

export default EmployeeList;
